import React, { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { supabaseClient } from '../../utils/supabase';
import { queryPolicies } from '../../utils/queryPolicies';
import './MaterializedReportStatus.css';

/**
 * Freshness of a report whose numbers come from materialized views that a
 * background job refreshes (public.materialized_report,
 * supabase/pg/reports/materialized_reports.sql).
 */
export interface MaterializedReportState {
  name: string;
  /** The time the numbers are as of; null until the first refresh finishes. */
  refreshed_at: string | null;
  refreshing: boolean;
  /** A refresh is queued and starts within seconds. */
  requested: boolean;
  duration_ms: number | null;
  refresh_every_seconds: number;
  /** The last refresh failed; the numbers shown are from the one before. */
  last_error: string | null;
}

export const materializedReportKey = (name: string) => ['materialized-report', name] as const;

// The materialized report RPCs postdate the generated database types.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabaseClient as any;

async function fetchStatus(name: string): Promise<MaterializedReportState | null> {
  const { data, error } = await db.rpc('materialized_report_status', { p_names: [name] });
  if (error) throw new Error(error.message || 'Could not read the report status');
  return (data as MaterializedReportState[])[0] ?? null;
}

async function requestRefresh(name: string): Promise<MaterializedReportState> {
  const { data, error } = await db.rpc('materialized_report_request_refresh', { p_name: name });
  if (error) throw new Error(error.message || 'Could not start the refresh');
  return data as MaterializedReportState;
}

function ago(at: string, now: number): string {
  const minutes = Math.max(0, Math.round((now - new Date(at).getTime()) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  const days = Math.round(hours / 24);
  return `${days} days ago`;
}

/** The registry name of a reports-section report backed by a materialized view. */
export const reportMaterializedName = (reportKey: string) => `report:${reportKey}`;

/**
 * Says how old a materialized report's numbers are and offers "Get latest
 * now". While a refresh is queued or running it polls; when the numbers
 * change it refetches `refetchKeys` and calls `onRefreshed` (for grids that
 * hold their own rows) so the report shows them.
 */
export function MaterializedReportStatus({ name, refetchKeys = [], onRefreshed }: {
  name: string;
  refetchKeys?: QueryKey[];
  onRefreshed?: () => void;
}): React.ReactElement | null {
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: materializedReportKey(name),
    queryFn: () => fetchStatus(name),
    ...queryPolicies.dashboardAggregate,
    refetchInterval: (query) => (query.state.data?.refreshing || query.state.data?.requested ? 3_000 : 60_000),
  });
  const refresh = useMutation({
    mutationFn: () => requestRefresh(name),
    onSuccess: (state) => queryClient.setQueryData(materializedReportKey(name), state),
  });
  const state = status.data;
  const seen = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (!state) return;
    if (seen.current !== undefined && state.refreshed_at !== seen.current) {
      refetchKeys.forEach((key) => queryClient.invalidateQueries({ queryKey: key }));
      onRefreshed?.();
    }
    seen.current = state.refreshed_at;
  }, [state, refetchKeys, onRefreshed, queryClient]);

  if (!state) return null;
  const busy = state.refreshing || state.requested;
  return (
    <div className="sky-mr-status" role="status" data-testid="materialized-report-status">
      {state.refreshed_at ? (
        <span>
          Numbers as of{' '}
          <time dateTime={state.refreshed_at} title={new Date(state.refreshed_at).toLocaleString()}>
            {new Date(state.refreshed_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} ({ago(state.refreshed_at, Date.now())})
          </time>
        </span>
      ) : (
        <span>These numbers are being prepared for the first time.</span>
      )}
      {busy ? (
        <span className="sky-mr-status__busy">Getting the latest numbers...</span>
      ) : (
        <button type="button" className="sky-mr-status__button" onClick={() => refresh.mutate()} disabled={refresh.isPending}>
          Get latest now
        </button>
      )}
      {state.last_error && !busy && <span className="sky-mr-status__error">The last refresh failed, so these are the numbers from before it.</span>}
      {refresh.error && <span className="sky-mr-status__error">{(refresh.error as Error).message}</span>}
    </div>
  );
}
